import React, {forwardRef, useState, useImperativeHandle, useLayoutEffect, useRef} from 'react'
import {Input} from './index.js'
import {InputRef} from './Input.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import {formatDuration} from '@utils/utils.js'

export interface TimePickerProps extends FieldProps, InvalidProps {
    // HH:mm, or with duration a number of minutes (true) or seconds ('seconds')
    value?: string | number
    placeholder?: string | number
    duration?: boolean | 'seconds'
    allowEmpty?: boolean
    // With duration, event.target.value is the duration as a numeric string
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export type TimePickerRef = FieldRef

const TimePicker = forwardRef<TimePickerRef, TimePickerProps>(function TimePicker({
    value = '',
    placeholder,
    duration,
    allowEmpty,
    onChange,
    label,
    info,
    readOnly,
    disabled,
    isInvalid,
    style
}, ref) {
    const inputRef = useRef<InputRef>(null)

    // Input handles the invalid state until the next change
    useImperativeHandle(ref, () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        setInvalid: (isInvalid = true) => inputRef.current?.setInvalid(isInvalid)
    }), [])

    // value can still be null from JS callers, the default only covers undefined
    const valueToTime = () => duration ? durationToTime(value, allowEmpty, duration) : `${value ?? ''}`

    const [currentTime, setCurrentTime] = useState<string>(valueToTime)

    // Layout effect so a new value is shown without a one-frame flash of the old one
    useLayoutEffect(() => {
        setCurrentTime(valueToTime())
    }, [value, duration, allowEmpty])

    const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled || readOnly) {
            return
        }

        let inputTime = event.target.value.replace('.', ':').replace(',', ':')

        if (/^$|^[0-9]{0,2}:?[0-9]{0,2}$/.test(inputTime)) {
            if (currentTime.length === 1 && inputTime.length === 2) {
                inputTime = `${inputTime}:`
            }

            setCurrentTime(inputTime)

            if (duration) {
                if (/^[0-9]{2}:[0-5][0-9]$/.test(inputTime)) {
                    event.target.value = timeToDuration(inputTime, duration).toString()
                    onChange?.(event)
                }
            } else if (/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(inputTime)) {
                // So a typed '.' or ',' separator reaches the caller as ':'
                event.target.value = inputTime
                onChange?.(event)
            }
        }

        if (allowEmpty && inputTime === '') {
            onChange?.(event)
        }
    }

    const handleBlur = () => {
        setCurrentTime(valueToTime())
    }

    return (
        <Input
            ref={inputRef}
            style={style}
            label={label}
            info={info}
            value={currentTime}
            placeholder={duration && placeholder ? durationToTime(placeholder, allowEmpty, duration) : placeholder}
            disabled={disabled}
            readOnly={readOnly}
            isInvalid={isInvalid}
            onChange={handleChangeInput}
            onBlur={handleBlur}
            append='mdi mdi-clock-outline'
            // '.' and ',' are accepted as separator too, handleChangeInput turns them into ':'
            regex={/^$|^[0-9]{0,2}[:.,]?[0-9]{0,2}$/}
        />
    )
})

// An empty duration ('', null, undefined) counts as 0, like formatDuration does for seconds
function durationToTime(duration: string | number, allowEmpty = false, unit: boolean | 'seconds'): string {
    if (allowEmpty && (duration === 0 || duration === '' || duration === null || duration === undefined)) {
        return ''
    }

    if (unit === 'seconds') {
        return formatDuration(duration)
    }

    const totalMinutes = parseInt(`${duration}`, 10) || 0
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}`
}

function timeToDuration(time: string, unit: boolean | 'seconds'): number {
    const parts = time.split(':')

    if (parts.length !== 2) {
        return 0
    }

    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)

    if (isNaN(hours) || isNaN(minutes)) {
        return 0
    }

    if (unit === 'seconds') {
        return (hours * 60 + minutes) * 60
    }

    return hours * 60 + minutes
}

export default TimePicker
